// Microsoft Graph helpers for people search. In Entra mode the requestor /
// use_case_owner typeahead searches the whole Unisys directory via Graph
// (delegated User.Read.All). In dev mode — or if Graph is unavailable / not
// consented — it falls back to the app's local users table.
import { pca } from './msal'
import { AUTH_MODE } from './config'
import { api, type UserRef } from '@/api/client'

const GRAPH = 'https://graph.microsoft.com/v1.0'

interface GraphUser {
    id: string
    displayName?: string
    mail?: string
    userPrincipalName?: string
}

function mapGraphUser(u: GraphUser): UserRef {
    return {
        id: u.id,
        name: u.displayName || u.userPrincipalName || u.id,
        email: u.mail || u.userPrincipalName || '',
    }
}

// Silently acquire a Graph token for User.Read.All. Returns null in dev mode, if
// there's no account, or if the scope isn't consented (caller falls back).
async function graphToken(): Promise<string | null> {
    if (AUTH_MODE !== 'entra' || !pca) return null
    const account = pca.getActiveAccount() ?? pca.getAllAccounts()[0]
    if (!account) return null
    try {
        const res = await pca.acquireTokenSilent({ scopes: ['User.Read.All'], account })
        return res.accessToken
    } catch {
        return null
    }
}

// Header carrying the user's own Graph token, so the backend can look up their
// line manager. Empty in dev mode or without consent; submission still works.
export async function graphAuthHeader(): Promise<Record<string, string>> {
    const token = await graphToken()
    return token ? { 'X-Graph-Authorization': `Bearer ${token}` } : {}
}

// Search all Unisys users. Graph directory search in Entra mode; local users
// table otherwise.
export async function searchDirectory(q: string): Promise<UserRef[]> {
    const token = await graphToken()
    if (!token) return api.searchUsers(q)

    const term = q.replace(/"/g, '').trim()
    if (term.length < 2) return []
    // $search does substring matching across the listed fields; combining it with
    // $filter (members only) is an advanced query — needs $count=true plus the
    // ConsistencyLevel: eventual header.
    const search = encodeURIComponent(
        `"displayName:${term}" OR "mail:${term}" OR "userPrincipalName:${term}"`,
    )
    const filter = encodeURIComponent("userType eq 'Member'")
    const url =
        `${GRAPH}/users?$search=${search}&$filter=${filter}&$count=true` +
        `&$top=15&$select=id,displayName,mail,userPrincipalName`
    try {
        const r = await fetch(url, {
            headers: { Authorization: `Bearer ${token}`, ConsistencyLevel: 'eventual' },
        })
        if (!r.ok) return api.searchUsers(q)
        const data = (await r.json()) as { value?: GraphUser[] }
        return (data.value ?? []).map(mapGraphUser)
    } catch {
        return api.searchUsers(q)
    }
}

// Resolve an id → profile for display. Local cache first (fast, works in dev),
// then Graph as a fallback for directory users not yet cached.
export async function resolveUser(id: string): Promise<UserRef | null> {
    try {
        const u = await api.getUser(id)
        if (u && u.name) return u
    } catch {
        /* fall through to Graph */
    }
    const token = await graphToken()
    if (!token) return null
    try {
        const r = await fetch(
            `${GRAPH}/users/${encodeURIComponent(id)}?$select=id,displayName,mail,userPrincipalName`,
            { headers: { Authorization: `Bearer ${token}` } },
        )
        if (!r.ok) return null
        return mapGraphUser((await r.json()) as GraphUser)
    } catch {
        return null
    }
}

// Profile photos are fetched once per person and kept as object URLs for the
// life of the page. null means "no photo" (or Graph unavailable): callers keep
// showing initials.
const photoCache = new Map<string, Promise<string | null>>()

export function userPhoto(id: string): Promise<string | null> {
    if (!id) return Promise.resolve(null)
    let p = photoCache.get(id)
    if (!p) {
        p = (async () => {
            const token = await graphToken()
            if (!token) return null
            try {
                const r = await fetch(`${GRAPH}/users/${encodeURIComponent(id)}/photos/96x96/$value`, {
                    headers: { Authorization: `Bearer ${token}` },
                })
                if (!r.ok) return null
                return URL.createObjectURL(await r.blob())
            } catch {
                return null
            }
        })()
        photoCache.set(id, p)
    }
    return p
}
